import {useEffect, useState} from 'react'
import {
    Alert,
    Button,
    Divider,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import styled from 'styled-components'
import {FlowApi} from '../../services/FlowApi.service'
import {DOC_TYPES} from '../../constants/documentTypes'
import {
    TemplateConfig,
    TemplateConfigAlignment,
    TemplateConfigKey,
    TemplateConfigOrientation,
    TemplateConfigPageSize,
} from '../../types/templateConfig'

const ALIGNMENTS: TemplateConfigAlignment[] = ['left', 'center', 'right']
const PAGE_SIZES: TemplateConfigPageSize[] = ['A4', 'Letter']
const ORIENTATIONS: TemplateConfigOrientation[] = ['portrait', 'landscape']

const FormBox = styled.div`
    width: 100%;
    max-width: 898px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    border: 1px solid #d0d7de;
    border-radius: 8px;
    padding: 24px;
    background-color: #ffffff;
    margin: 24px auto 0;
    box-sizing: border-box;
`

const FormTitle = styled.h3`
    font-family: Roboto, sans-serif;
    font-weight: 600;
    font-size: 18px;
    color: #1f2328;
    margin: 0;
`

const Row = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    align-items: center;
`

const KeyRow = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
`

const emptyKey = (): TemplateConfigKey => ({doc_type: '', key_str: 'default'})

const blankConfig = (): TemplateConfig => ({
    name: '',
    keys: [emptyKey()],
    header: {image_url: null, alignment: 'center'},
    footer: {text: '', font: 'Arial', font_size: 9, alignment: 'left'},
    page: {
        size: 'A4',
        margin_top_cm: 3,
        margin_bottom_cm: 2,
        margin_left_cm: 3,
        margin_right_cm: 2,
        orientation: 'portrait',
    },
})

type Props = {
    flowId: string
    organizationId: string
}

export default function TemplateConfigsSection({flowId, organizationId}: Props) {
    const flowApi = new FlowApi()

    const [configs, setConfigs] = useState<TemplateConfig[]>([])
    const [selectedName, setSelectedName] = useState<string | null>(null)
    const [form, setForm] = useState<TemplateConfig>(blankConfig())
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const [logoFile, setLogoFile] = useState<File | null>(null)
    const [uploadingLogo, setUploadingLogo] = useState(false)
    const [logoMessage, setLogoMessage] = useState<string | null>(null)

    const loadConfigs = async () => {
        if (!flowId) {
            setConfigs([])
            return
        }
        setLoading(true)
        setError(null)
        try {
            const data = await flowApi.getTemplateConfigs(flowId)
            setConfigs(Array.isArray(data) ? data : [])
        } catch (err) {
            setError('Erro ao carregar os templates configurados.')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        loadConfigs()
        setSelectedName(null)
        setForm(blankConfig())
    }, [flowId])

    const handleSelect = (name: string) => {
        const found = configs.find((c) => c.name === name)
        if (!found) return
        setSelectedName(name)
        setForm({...blankConfig(), ...found})
        setError(null)
    }

    const handleNew = () => {
        setSelectedName(null)
        setForm(blankConfig())
        setError(null)
    }

    const updateKey = (index: number, patch: Partial<TemplateConfigKey>) => {
        setForm((prev) => ({
            ...prev,
            keys: prev.keys.map((k, i) => (i === index ? {...k, ...patch} : k)),
        }))
    }

    const addKey = () => {
        setForm((prev) => ({...prev, keys: [...prev.keys, emptyKey()]}))
    }

    const removeKey = (index: number) => {
        setForm((prev) => ({
            ...prev,
            keys: prev.keys.filter((_, i) => i !== index),
        }))
    }

    const handleSave = async () => {
        if (!flowId) return
        if (!form.name.trim() || form.keys.some((k) => !k.doc_type)) {
            setError('Preencha o nome e o tipo de documento de todas as chaves.')
            return
        }

        setSaving(true)
        setError(null)
        try {
            if (selectedName) {
                await flowApi.updateTemplateConfig(flowId, selectedName, form)
            } else {
                await flowApi.createTemplateConfig(flowId, form)
            }
            await loadConfigs()
            setSelectedName(form.name)
        } catch (err) {
            setError('Erro ao salvar o template config.')
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async () => {
        if (!flowId || !selectedName) return
        setSaving(true)
        setError(null)
        try {
            await flowApi.deleteTemplateConfig(flowId, selectedName)
            await loadConfigs()
            handleNew()
        } catch (err) {
            setError('Erro ao excluir o template config.')
        } finally {
            setSaving(false)
        }
    }

    const handleLogoUpload = async () => {
        if (!organizationId || !logoFile) return
        setUploadingLogo(true)
        setLogoMessage(null)
        try {
            await flowApi.uploadOrganizationLogo(organizationId, logoFile)
            setLogoMessage('Logo enviado com sucesso.')
            setLogoFile(null)
        } catch (err) {
            setLogoMessage('Erro ao enviar o logo.')
        } finally {
            setUploadingLogo(false)
        }
    }

    if (!flowId) {
        return null
    }

    return (
        <FormBox>
            <FormTitle>Templates de documento (novo)</FormTitle>

            {error && <Alert severity="error">{error}</Alert>}

            <Row>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleNew}
                >
                    Novo template config
                </Button>
            </Row>

            {configs.length > 0 && (
                <List dense sx={{border: '1px solid #d0d7de', borderRadius: '6px'}}>
                    {configs.map((c) => (
                        <ListItemButton
                            key={c.name}
                            selected={c.name === selectedName}
                            onClick={() => handleSelect(c.name)}
                        >
                            <ListItemText primary={c.name} secondary={`${c.keys.length} chave(s)`} />
                        </ListItemButton>
                    ))}
                </List>
            )}

            <Divider />

            <TextField
                label="Nome do template"
                size="small"
                value={form.name}
                disabled={!!selectedName}
                onChange={(e) => setForm((prev) => ({...prev, name: e.target.value}))}
                helperText={selectedName ? 'O nome não pode ser alterado após criado.' : ''}
            />

            <Typography variant="subtitle2">Chaves (tipo de documento + variação)</Typography>
            {form.keys.map((key, index) => (
                <KeyRow key={index}>
                    <TextField
                        select
                        size="small"
                        label="Tipo de documento"
                        value={key.doc_type}
                        onChange={(e) => updateKey(index, {doc_type: e.target.value})}
                        sx={{minWidth: '220px'}}
                    >
                        {DOC_TYPES.map((doc) => (
                            <MenuItem key={doc.apiId} value={doc.apiId}>
                                {doc.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        size="small"
                        label="Chave"
                        value={key.key_str}
                        onChange={(e) => updateKey(index, {key_str: e.target.value})}
                    />
                    <IconButton
                        size="small"
                        onClick={() => removeKey(index)}
                        disabled={form.keys.length <= 1}
                        aria-label="Remover chave"
                    >
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </KeyRow>
            ))}
            <Button size="small" onClick={addKey} sx={{alignSelf: 'flex-start'}}>
                + Adicionar chave
            </Button>

            <Typography variant="subtitle2">Cabeçalho</Typography>
            <Row>
                <TextField
                    select
                    size="small"
                    label="Alinhamento"
                    value={form.header?.alignment ?? 'center'}
                    onChange={(e) =>
                        setForm((prev) => ({
                            ...prev,
                            header: {
                                image_url: prev.header?.image_url ?? null,
                                alignment: e.target.value as TemplateConfigAlignment,
                            },
                        }))
                    }
                >
                    {ALIGNMENTS.map((a) => (
                        <MenuItem key={a} value={a}>
                            {a}
                        </MenuItem>
                    ))}
                </TextField>
            </Row>

            <Typography variant="subtitle2">Rodapé</Typography>
            <Row>
                <TextField
                    size="small"
                    label="Texto do rodapé"
                    value={form.footer?.text ?? ''}
                    onChange={(e) =>
                        setForm((prev) => ({
                            ...prev,
                            footer: {
                                text: e.target.value,
                                font: prev.footer?.font ?? 'Arial',
                                font_size: prev.footer?.font_size ?? 9,
                                alignment: prev.footer?.alignment ?? 'left',
                            },
                        }))
                    }
                    sx={{flex: 1, minWidth: '220px'}}
                />
                <TextField
                    size="small"
                    label="Fonte"
                    value={form.footer?.font ?? ''}
                    onChange={(e) =>
                        setForm((prev) => ({
                            ...prev,
                            footer: {
                                text: prev.footer?.text ?? '',
                                font: e.target.value,
                                font_size: prev.footer?.font_size ?? 9,
                                alignment: prev.footer?.alignment ?? 'left',
                            },
                        }))
                    }
                    sx={{maxWidth: '160px'}}
                />
                <TextField
                    size="small"
                    type="number"
                    label="Tamanho da fonte"
                    value={form.footer?.font_size ?? 9}
                    onChange={(e) =>
                        setForm((prev) => ({
                            ...prev,
                            footer: {
                                text: prev.footer?.text ?? '',
                                font: prev.footer?.font ?? 'Arial',
                                font_size: Number(e.target.value),
                                alignment: prev.footer?.alignment ?? 'left',
                            },
                        }))
                    }
                    sx={{maxWidth: '140px'}}
                />
                <TextField
                    select
                    size="small"
                    label="Alinhamento"
                    value={form.footer?.alignment ?? 'left'}
                    onChange={(e) =>
                        setForm((prev) => ({
                            ...prev,
                            footer: {
                                text: prev.footer?.text ?? '',
                                font: prev.footer?.font ?? 'Arial',
                                font_size: prev.footer?.font_size ?? 9,
                                alignment: e.target.value as TemplateConfigAlignment,
                            },
                        }))
                    }
                >
                    {ALIGNMENTS.map((a) => (
                        <MenuItem key={a} value={a}>
                            {a}
                        </MenuItem>
                    ))}
                </TextField>
            </Row>

            <Typography variant="subtitle2">Página</Typography>
            <Row>
                <TextField
                    select
                    size="small"
                    label="Tamanho"
                    value={form.page?.size ?? 'A4'}
                    onChange={(e) =>
                        setForm((prev) => ({
                            ...prev,
                            page: {
                                size: e.target.value as TemplateConfigPageSize,
                                margin_top_cm: prev.page?.margin_top_cm ?? 3,
                                margin_bottom_cm: prev.page?.margin_bottom_cm ?? 2,
                                margin_left_cm: prev.page?.margin_left_cm ?? 3,
                                margin_right_cm: prev.page?.margin_right_cm ?? 2,
                                orientation: prev.page?.orientation ?? 'portrait',
                            },
                        }))
                    }
                >
                    {PAGE_SIZES.map((s) => (
                        <MenuItem key={s} value={s}>
                            {s}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    size="small"
                    label="Orientação"
                    value={form.page?.orientation ?? 'portrait'}
                    onChange={(e) =>
                        setForm((prev) => ({
                            ...prev,
                            page: {
                                size: prev.page?.size ?? 'A4',
                                margin_top_cm: prev.page?.margin_top_cm ?? 3,
                                margin_bottom_cm: prev.page?.margin_bottom_cm ?? 2,
                                margin_left_cm: prev.page?.margin_left_cm ?? 3,
                                margin_right_cm: prev.page?.margin_right_cm ?? 2,
                                orientation: e.target.value as TemplateConfigOrientation,
                            },
                        }))
                    }
                >
                    {ORIENTATIONS.map((o) => (
                        <MenuItem key={o} value={o}>
                            {o}
                        </MenuItem>
                    ))}
                </TextField>
                {(
                    [
                        ['margin_top_cm', 'Margem superior (cm)'],
                        ['margin_bottom_cm', 'Margem inferior (cm)'],
                        ['margin_left_cm', 'Margem esquerda (cm)'],
                        ['margin_right_cm', 'Margem direita (cm)'],
                    ] as const
                ).map(([field, label]) => (
                    <TextField
                        key={field}
                        size="small"
                        type="number"
                        label={label}
                        value={form.page?.[field] ?? 0}
                        onChange={(e) =>
                            setForm((prev) => ({
                                ...prev,
                                page: {
                                    size: prev.page?.size ?? 'A4',
                                    margin_top_cm: prev.page?.margin_top_cm ?? 3,
                                    margin_bottom_cm: prev.page?.margin_bottom_cm ?? 2,
                                    margin_left_cm: prev.page?.margin_left_cm ?? 3,
                                    margin_right_cm: prev.page?.margin_right_cm ?? 2,
                                    orientation: prev.page?.orientation ?? 'portrait',
                                    [field]: Number(e.target.value),
                                },
                            }))
                        }
                        sx={{maxWidth: '160px'}}
                    />
                ))}
            </Row>

            <Row>
                <Button
                    variant="contained"
                    size="small"
                    onClick={handleSave}
                    disabled={saving || loading}
                    sx={{
                        backgroundColor: '#3ACDB4',
                        '&:hover': {backgroundColor: '#3ACDB4'},
                    }}
                >
                    {selectedName ? 'Atualizar template' : 'Criar template'}
                </Button>
                {selectedName && (
                    <Button
                        variant="text"
                        size="small"
                        color="error"
                        onClick={handleDelete}
                        disabled={saving}
                    >
                        Excluir template
                    </Button>
                )}
            </Row>

            <Divider />

            <Typography variant="subtitle2">Logo da organização</Typography>
            {logoMessage && (
                <Alert severity={logoMessage.startsWith('Erro') ? 'error' : 'success'}>
                    {logoMessage}
                </Alert>
            )}
            <Row>
                <input
                    type="file"
                    accept="image/png,image/jpeg"
                    onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)}
                />
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleLogoUpload}
                    disabled={!logoFile || !organizationId || uploadingLogo}
                >
                    {uploadingLogo ? 'Enviando...' : 'Enviar logo'}
                </Button>
            </Row>
            {!organizationId && (
                <Typography variant="caption" color="text.secondary">
                    Selecione uma organização para enviar o logo.
                </Typography>
            )}
        </FormBox>
    )
}
